import React, { useEffect } from 'react';
import {   InfoBox,   ExerciseBox,   CodeBlock, CodeEditor, BoldTextParagraph,   InlineCode,   SectionTitle,  Note,  TableContainer,  Highlight,  PatternContainer,  SectionHeading,  SubHeading,  TwoColGrid,  ThreeColGrid,  GridItem,  Divider,  MutedText, Separator, CorrectionBox} from '../../../../components/course/CourseComponents';

const Chapitre4Content = ({ courseId = 'baseprogrammation' }) => {
  
  // Effet pour donner le focus à l'éditeur quand le composant se monte
  useEffect(() => {
    // Le focus sera géré par la prop autoFocus du CodeEditor
  }, []);

  return (
    <>
     <InfoBox title="Objectif" color="blue">
        <p className="mb-0">
        Exécuter du code seulement si une condition est vraie.
        </p>
      </InfoBox>       

      <Separator type="text" text="Le mot-clé if (qui veut dire si)" color="gray-darker" size="md" />
      <SectionTitle>Qu'est-ce qu'une condition ? </SectionTitle>
      <p>
        Un jeu vidéo ne se contente pas d'exécuter des lignes de code les unes après les autres. Il réagit aux événements. 
        Pour cela, on utilise des conditions.<br/>
        Ce sont ces conditions qui rendent le jeu intéressant et interactif.
      </p>

      <p>Par exemple :</p>
      <ul className="list-disc list-inside mb-4">
        <li>Si la vie du joueur est en dessous de 0, alors il a perdu</li>
        <li>Si le joueur appuie sur la touche gauche, alors il se déplace</li>
        <li>Si le temps est écoulé, alors la partie est finie</li>
      </ul>

      <p>
        En JavaScript, une condition s'écrit avec le mot-clé <InlineCode>if</InlineCode> qui veut dire <strong>"si"</strong> en français.
      </p>

      <Note type="gray" title="🔧 Exemple de condition :">
        <CodeBlock language="javascript">
{`let vie = -1;
if (vie < 0) {
  print("Le joueur a perdu");
}`}
        </CodeBlock>
      </Note>

      <Note type="info" title="💡 Remarque :">
        Tu peux aussi écrire sans accolades pour une seule instruction :
        <CodeBlock language="javascript">
{`if (vie < 0)
  print("Le joueur a perdu");`}
        </CodeBlock>
        Mais on recommande d'utiliser toujours les <InlineCode>{`{}`}</InlineCode> pour plus de clarté.
      </Note>

      <Note type="tip" title="💡 Astuce :">
        L'accolade sur ton clavier se fait en appuyant simultanément sur la touche <InlineCode>AltGr</InlineCode> et <InlineCode>4</InlineCode><br/><InlineCode>AltGr</InlineCode> et <InlineCode>+</InlineCode> pour la fermer.<br/>
      </Note>

      <Separator type="text" text="Le mot-clé else" color="gray-darker" size="md" />
      <SectionTitle>Le mot-clé else (qui veut dire sinon)</SectionTitle>
      <p>
        Si tu veux faire autre chose quand la condition n'est pas remplie, utilise <InlineCode>else</InlineCode> qui veut dire <strong>"sinon"</strong> en français :
      </p>

      <Note type="gray" title="🔧 Condition avec else :">
        <CodeBlock language="javascript">
{`if (vie <= 0) {
  print("Le joueur a perdu");
} else {
  print("Le joueur est vivant");
}`}
        </CodeBlock>
      </Note>

      <Note type="exercice" title="🧪 À toi de jouer :">
        Teste cette condition avec différentes valeurs de <InlineCode>vie</InlineCode> :
        <CodeBlock language="javascript">
{`let vie = 5; // essaye avec -1
if (vie <= 0) {
  print("Le joueur a perdu");
} else {
  print("Le joueur est vivant");
}`}
        </CodeBlock>
      </Note>

      <CodeEditor
        courseId={courseId}
        editorId="conditions-base"
        title="Première condition if/else"
        showConsole={true}
        height="250px"
        initialCode=""
        autoFocus={true}
      />

      <Separator type="text" text="Comparateurs utiles" color="gray-darker" size="md" />
      <SectionTitle>Comparateurs utiles</SectionTitle>
      <p>
        Voici les opérateurs de comparaison que tu peux utiliser :
      </p>

      <Note type="gray" title="🔧 Comparateurs :">
        <ul className="list-disc list-inside">
          <li><InlineCode>&gt;</InlineCode> : supérieur à</li>
          <li><InlineCode>&lt;</InlineCode> : inférieur à</li>
          <li><InlineCode>&gt;=</InlineCode> : supérieur ou égal à</li>
          <li><InlineCode>&lt;=</InlineCode> : inférieur ou égal à</li>
          <li><InlineCode>==</InlineCode> : égal à (valeur)</li>
          <li><InlineCode>!=</InlineCode> : différent de</li>
        </ul>
      </Note>

      <Note type="info" title="⚠️ Attention :">
        <CodeBlock language="javascript">
{`if (age = 18) // faux ! on assigne la valeur 18 à la variable age
if (age == 18) // correct ! on compare`}
        </CodeBlock>
      </Note>

      <Note type="exercice" title="🧪 Exercice majorité :">
        Affiche "majeur" ou "mineur" selon la valeur de la variable <InlineCode>age</InlineCode> :<br/>
        - Crée une variable <InlineCode>age</InlineCode> avec ton age<br/>
        - Utilise une condition <InlineCode>if/else</InlineCode> pour comparer <InlineCode>age</InlineCode> avec 18<br/>
        - Affiche "majeur" si <InlineCode>age</InlineCode> est supérieur ou égal à 18, sinon affiche "mineur"<br/>
        <br/>Teste avec différentes valeurs d'âge !
        
        <CorrectionBox>
          <CodeBlock language="javascript">
{`let age = 16;
if (age >= 18) {
  print("majeur");
} else {
  print("mineur");
}`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <CodeEditor
        courseId={courseId}
        editorId="conditions-majorite"
        title="Exercice : Majorité"
        showConsole={true}
        height="250px"
        initialCode=""
      />

      <Separator type="text" text="Conditions multiples" color="gray-darker" size="md" />
      <SectionTitle>Conditions multiples</SectionTitle>
      <p>
        Tu peux combiner plusieurs conditions :
      </p>

      <Note type="gray" title="🔧 Combinaison de conditions :">
        <CodeBlock language="javascript">
{`if (age >= 12 && age < 18) {
  print("adolescent");
}`}
        </CodeBlock>
        <ul className="list-disc list-inside mt-2">
          <li><InlineCode>&&</InlineCode> veut dire ET (les deux conditions doivent être vraies)</li>
          <li><InlineCode>||</InlineCode> veut dire OU (au moins une condition doit être vraie)</li>
        </ul>
      </Note>

      <Separator type="text" text="Conditions enchaînées (else if)" color="gray-darker" size="md" />
      <SectionTitle>Conditions enchaînées (else if)</SectionTitle>
      <p>
        Pour tester plusieurs conditions à la suite, utilise <InlineCode>else if</InlineCode> qui veut dire <strong>"sinon si"</strong> en français :
      </p>

      <Note type="gray" title="🔁 Conditions enchaînées :">
        <CodeBlock language="javascript">
{`if (nombre < 10) {
  ...
} else if (nombre < 50) {
  ...
} else if (nombre < 100)  {
  ...
} else {
  ...
}`}
        </CodeBlock>
      </Note>

      <Note type="exercice" title="🧪 Exercice complet :">
        Écris un programme qui affiche la catégorie d'âge selon la valeur de <InlineCode>age</InlineCode> :<br/>
        - Crée une variable <InlineCode>age</InlineCode> avec ton age<br/>
        - Si l'âge est inférieur à 12 : afficher "enfant"<br/>
        - Si l'âge est entre 12 et 17 (inclus) : afficher "adolescent"<br/>
        - Sinon : afficher "adulte"<br/><br/>
        Teste avec différentes valeurs d'âge !
        
        <CorrectionBox>
          <CodeBlock language="javascript">
{`let age = 15;
if (age < 12) {
  print("enfant");
} else if (age < 18) {
  print("adolescent");
} else {
  print("adulte");
}`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <CodeEditor
        courseId={courseId}
        editorId="conditions-categories"
        title="Exercice : Catégories d'âge"
        showConsole={true}
        height="300px"
        initialCode=""
      />

      <p>
        ✅ Voilà pour les conditions. Grâce à elles, ton code devient intelligent : il ne s'exécute plus de façon linéaire, mais change son comportement en fonction de tes règles !
      </p>
      
      <BoldTextParagraph>
        Prochaine étape : les boucles !
      </BoldTextParagraph>

  </>
  );
};

export default Chapitre4Content; 